"use client";

/**
 * Filterable product picker with an optional catalog header.
 *
 * Options render as IntakeOptionCard whether or not the header is present,
 * so a product's description sits beneath its name exactly as on every other
 * selection step (ELI-1103). The header only adds the counts-and-filters strip
 * and the panel it sits in; it does not change how an option looks.
 *
 * The Marketplace lookup and the fit notices sit below the selection group:
 * they are affordances around the choice, not choices themselves.
 */

import { type ReactNode, useId, useState } from "react";
import {
  IntakeOptionCard,
  IntakeOptionInputNameProvider,
} from "./IntakeOptionCard";
import {
  MarketplaceProductLookup,
  type MarketplaceProductLookupProps,
} from "./MarketplaceProductLookup";
import {
  ProductFitChecking,
  type ProductFitCheckingProps,
} from "./ProductFitChecking";
import {
  ProductFitWarning,
  type ProductFitWarningProps,
} from "./ProductFitWarning";

export interface ProductItem {
  name: string;
  vendor: string;
  bg: string;
  fg: string;
  mark: string;
  /** Product logo URL. The `{bg, fg, mark}` initial stays the fallback — for a
   *  missing logo and for one whose image fails to load. */
  logo?: string;
  maturity?: string;
  kind?: string;
  integ?: string[];
  desc?: string;
  fit?: string[];
}

export interface IntakeProductPickerHeader {
  /** Filtered catalog count, including results outside the current page. */
  totalProducts: number;
  maturity?: "emerging" | "established" | "";
  onMaturityChange?: (value: "emerging" | "established" | "") => void;
  /** Declared integrations from the unfiltered product result set. */
  integrationOptions?: Array<{ id: string; name: string }>;
  integrationProductId?: string;
  onIntegrationProductIdChange?: (id: string) => void;
}

export interface IntakeProductPickerProps {
  products: Array<ProductItem & { id: string }>;
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  /** Accessible name for the selection group. */
  ariaLabel: string;
  /** Selection cardinality — drives the option indicator and the group role. */
  selectionMode?: "single" | "multiple";
  /** When supplied, renders the bordered catalog panel and its filters. */
  header?: IntakeProductPickerHeader;
  /** Rendered inside the group after the cards — the refine row. */
  trailing?: ReactNode;
  marketplaceLookup?: MarketplaceProductLookupProps;
  /** Visible progress state while the current Marketplace selection is checked. */
  fitChecking?: ProductFitCheckingProps;
  /** Advisory fit warning for the current selection. Advisory only — it never
   *  gates the picker or the surrounding flow. */
  fitWarning?: ProductFitWarningProps;
}

const filterSelectStyle = {
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888c93' stroke-width='2.5'><path d='M6 9l6 6 6-6'/></svg>\")",
  backgroundPosition: "right 8px center",
  backgroundRepeat: "no-repeat",
};

/** The option's leading square: the product's logo when it has one that
 *  loads, and the deterministic `{bg, fg, mark}` initial otherwise. Its own
 *  component so each row keeps its own load-failure state. */
function ProductAvatar({ product }: { product: ProductItem }) {
  // Keyed by URL rather than a boolean, so a row that swaps in a different
  // logo gets a fresh attempt instead of staying stuck on the initial.
  const [failedLogo, setFailedLogo] = useState<string>();
  const showLogo = Boolean(product.logo) && failedLogo !== product.logo;

  return (
    <span
      aria-hidden="true"
      className="flex h-[34px] w-[34px] items-center justify-center overflow-hidden rounded-(--radius-sm) text-[14px] font-semibold"
      style={{ background: product.bg, color: product.fg }}
    >
      {showLogo ? (
        <img
          src={product.logo}
          alt=""
          className="h-full w-full object-contain"
          onError={() => setFailedLogo(product.logo)}
        />
      ) : (
        product.mark
      )}
    </span>
  );
}

export function IntakeProductPicker({
  products,
  selectedIds = [],
  onToggle,
  ariaLabel,
  selectionMode = "single",
  header,
  trailing,
  marketplaceLookup,
  fitChecking,
  fitWarning,
}: IntakeProductPickerProps) {
  const inputName = `intake-product-${useId()}`;
  const indicator = selectionMode === "single" ? "radio" : "checkbox";

  return (
    <div
      className={
        header
          ? "overflow-hidden rounded-(--radius-container) border border-(--border-default) bg-(--bg-white) shadow-(--shadow-xs)"
          : undefined
      }
    >
      {header && (
        <div className="flex flex-wrap items-center gap-3 border-b border-(--border-subtle) bg-(--bg-canvas) px-[14px] py-[10px]">
          <span className="text-[11.5px] font-semibold text-(--fg-secondary)">
            {products.length} of {header.totalProducts} products ·{" "}
            {selectedIds.length} selected
          </span>
          <div className="ml-auto flex flex-wrap gap-[7px]">
            {header.onMaturityChange && (
              <select
                value={header.maturity ?? ""}
                onChange={(event) =>
                  header.onMaturityChange?.(
                    event.target.value as "emerging" | "established" | "",
                  )
                }
                aria-label="Filter by vendor maturity"
                style={filterSelectStyle}
                className="cursor-pointer appearance-none rounded-(--radius-field) border border-(--border-default) bg-(--bg-white) py-[5px] pr-[26px] pl-[10px] text-[11.5px] font-semibold text-(--fg-muted)"
              >
                <option value="">Maturity</option>
                <option value="established">Established</option>
                <option value="emerging">Emerging</option>
              </select>
            )}
            {header.onIntegrationProductIdChange && (
              <select
                value={header.integrationProductId ?? ""}
                onChange={(event) =>
                  header.onIntegrationProductIdChange?.(event.target.value)
                }
                aria-label="Filter by integration"
                style={filterSelectStyle}
                className="cursor-pointer appearance-none rounded-(--radius-field) border border-(--border-default) bg-(--bg-white) py-[5px] pr-[26px] pl-[10px] text-[11.5px] font-semibold text-(--fg-muted)"
              >
                <option value="">Integrates with</option>
                {(header.integrationOptions ?? []).map((integration) => (
                  <option key={integration.id} value={integration.id}>
                    {integration.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      <IntakeOptionInputNameProvider name={inputName}>
        {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: selectionMode restricts this to group or radiogroup, which both support accessible names */}
        <div
          role={selectionMode === "single" ? "radiogroup" : "group"}
          aria-label={ariaLabel}
          // Inside the catalog panel the cards need breathing room from its
          // border; standalone they are the outermost chrome.
          className={["flex flex-col gap-2", header ? "p-2" : ""].join(" ")}
        >
          {products.map((product) => (
            <IntakeOptionCard
              key={product.id}
              name={product.name}
              secondaryLabel={product.vendor || undefined}
              description={product.desc}
              indicator={indicator}
              selected={selectedIds.includes(product.id)}
              onSelect={() => onToggle?.(product.id)}
              adornment={<ProductAvatar product={product} />}
            />
          ))}
          {trailing}
        </div>
      </IntakeOptionInputNameProvider>
      {marketplaceLookup && <MarketplaceProductLookup {...marketplaceLookup} />}
      {fitChecking && <ProductFitChecking {...fitChecking} />}
      {fitWarning && <ProductFitWarning {...fitWarning} />}
    </div>
  );
}
